import React, { useEffect, useState } from 'react';
import GatewayV2Services from '../../services/externalServices/TicketServices/gatewayV2Services';
import PricingConfigServices from '../../services/externalServices/TicketServices/pricingConfigServices';

const emptyForm = {
  code: '',
  displayName: '',
  active: true,
  methods: 'UPI,CreditCard,DebitCard,Netbanking',
  capabilities: '{"redirect":true,"qr":false,"sdk":false,"refund":true,"status":true}',
  auth: '{"type":"oauth_password","tokenUrl":"","tokenFields":{}}',
  createPayment: '{"method":"POST","urlTemplate":"","headersTemplate":{},"bodyTemplate":{},"responseMap":{"redirectUrlPath":"redirect_url","paymentIdPath":"payment.id","merchantOrderPath":"merchant_order.id"}}',
  status: '{"method":"GET","urlTemplate":"","headersTemplate":{},"responseMap":{"statusPath":"status","mappingTable":{}}}',
  refund: '{"method":"POST","urlTemplate":"","headersTemplate":{},"bodyTemplate":{},"responseMap":{"refundIdPath":"id","statusPath":"status"}}',
  refundStatus: '{"method":"GET","urlTemplate":"","headersTemplate":{},"responseMap":{"statusPath":"status","refundIdPath":"id"}}',
  refundUpdate: '{"method":"PATCH","urlTemplate":"","headersTemplate":{},"bodyTemplate":{},"responseMap":{"statusPath":"status","refundIdPath":"id"}}',
  voidPayment: '{"method":"POST","urlTemplate":"","headersTemplate":{},"bodyTemplate":{},"responseMap":{"statusPath":"status"}}',
  callback: '{"verify":"sha512","headerName":"Signature","secretRef":"UNLIMIT_CALLBACK_SECRET","encoding":"json","paths":{"merchantOrderId":"merchant_order.id","providerPaymentId":"payment.id","state":"status"}}',
  returnUrls: '{"webSuccess":"","webCancel":"","webDecline":"","nativeDeeplink":""}'
};

export default function Profiles() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [gatewayMaster, setGatewayMaster] = useState([]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await GatewayV2Services.listGatewayProfiles();
      setItems(res?.data || res?.items || []);
    } catch (e) {
      setError('Failed to load profiles');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const gm = await PricingConfigServices.listGatewaysMaster();
        const list = gm?.data || gm || [];
        setGatewayMaster(Array.isArray(list) ? list : []);
      } catch (_) { setGatewayMaster([]); }
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // Compact helpers to prevent accidental newlines breaking JSON (e.g., urlTemplate split across lines)
      const compact = (txt) => (txt || '').replace(/\r?\n+/g, ' ').replace(/\t+/g, ' ').replace(/\s{2,}/g, ' ').trim();
      const cCapabilities = compact(form.capabilities);
      const cAuth = compact(form.auth);
      const cCreate = compact(form.createPayment);
      const cStatus = compact(form.status);
      const cRefund = compact(form.refund);
      const cCallback = compact(form.callback);
      const cRefundStatus = compact(form.refundStatus);
      const cVoid = compact(form.voidPayment);
      const cRefundUpdate = compact(form.refundUpdate);
      const cReturnUrls = compact(form.returnUrls);

      const parsedCapabilities = safeJson(form.capabilities);
      const parsedAuth = safeJson(cAuth);
      const parsedCreate = safeJson(cCreate);
      const parsedStatus = safeJson(cStatus);
      const parsedRefund = safeJson(cRefund);
      const parsedCallback = safeJson(cCallback);
      const parsedRefundStatus = safeJson(cRefundStatus);
      const parsedVoid = safeJson(cVoid);
      const parsedRefundUpdate = safeJson(cRefundUpdate);
      const parsedReturnUrls = safeJson(cReturnUrls);

      // Basic validation to avoid silently saving empty objects from invalid JSON
      const invalidJsonEntered = (txt) => (txt && txt.trim() !== '' && txt.trim() !== '{}' && txt.trim() !== '[]');
      if (invalidJsonEntered(form.callback) && Object.keys(parsedCallback).length === 0) {
        setError('Callback JSON is invalid. Please fix formatting.');
        return;
      }
      if (invalidJsonEntered(form.createPayment) && Object.keys(parsedCreate).length === 0) {
        setError('createPayment JSON is invalid. Please fix formatting.');
        return;
      }
      if (invalidJsonEntered(form.status) && (Object.keys(parsedStatus).length === 0 || !parsedStatus.urlTemplate)) {
        setError('status JSON is invalid or missing urlTemplate.');
        return;
      }
      // New: validate refund/refundStatus/void when user entered non-empty text
      if (invalidJsonEntered(form.refund) && (Object.keys(parsedRefund).length === 0 || !parsedRefund.urlTemplate)) {
        setError('refund JSON is invalid or missing urlTemplate.');
        return;
      }
      if (invalidJsonEntered(form.refundStatus) && (Object.keys(parsedRefundStatus).length === 0 || !parsedRefundStatus.urlTemplate)) {
        setError('refundStatus JSON is invalid or missing urlTemplate.');
        return;
      }
      if (invalidJsonEntered(form.voidPayment) && (Object.keys(parsedVoid).length === 0 || !parsedVoid.urlTemplate)) {
        setError('voidPayment JSON is invalid or missing urlTemplate.');
        return;
      }
      if (invalidJsonEntered(form.refundUpdate) && (Object.keys(parsedRefundUpdate).length === 0 || !parsedRefundUpdate.urlTemplate)) {
        setError('refundUpdate JSON is invalid or missing urlTemplate.');
        return;
      }
      if (invalidJsonEntered(form.auth) && Object.keys(parsedAuth).length === 0) {
        setError('auth JSON is invalid.');
        return;
      }
      if (invalidJsonEntered(form.capabilities) && Object.keys(parsedCapabilities).length === 0) {
        setError('capabilities JSON is invalid.');
        return;
      }
      if (invalidJsonEntered(form.returnUrls) && Object.keys(parsedReturnUrls).length === 0) {
        setError('returnUrls JSON is invalid.');
        return;
      }

      const payload = {
        code: form.code.trim(),
        displayName: form.displayName.trim() || form.code.trim(),
        active: !!form.active,
        methods: form.methods.split(',').map(s => s.trim()).filter(Boolean),
        capabilities: parsedCapabilities,
        auth: parsedAuth,
        createPayment: parsedCreate,
        status: parsedStatus,
        refund: parsedRefund,
        callback: parsedCallback,
        refundStatus: parsedRefundStatus,
        refundUpdate: parsedRefundUpdate,
        voidPayment: parsedVoid,
        returnUrls: parsedReturnUrls,
      };
      if (editingId) await GatewayV2Services.updateGatewayProfile(editingId, payload);
      else await GatewayV2Services.createGatewayProfile(payload);
      setForm(emptyForm); setEditingId(null);
      load();
    } catch (e) {
      setError(e?.message || 'Save failed');
    }
  };

  const safeJson = (txt) => { try { return JSON.parse(txt || '{}'); } catch { return {}; } };

  const startEdit = (it) => {
    setEditingId(it._id);
    setForm({
      code: it.code || '',
      displayName: it.displayName || '',
      active: it.active !== false,
      methods: Array.isArray(it.methods) ? it.methods.join(',') : (it.methods || ''),
      capabilities: JSON.stringify(it.capabilities || {}, null, 2),
      auth: JSON.stringify(it.auth || {}, null, 2),
      createPayment: JSON.stringify(it.createPayment || {}, null, 2),
      status: JSON.stringify(it.status || {}, null, 2),
      refund: JSON.stringify(it.refund || {}, null, 2),
      callback: JSON.stringify(it.callback || {}, null, 2),
      refundStatus: JSON.stringify(it.refundStatus || {}, null, 2),
      voidPayment: JSON.stringify(it.voidPayment || {}, null, 2),
      refundUpdate: JSON.stringify(it.refundUpdate || {}, null, 2),
      returnUrls: JSON.stringify(it.returnUrls || {}, null, 2)
    });
  };

  const resetForm = () => { setForm(emptyForm); setEditingId(null); setError(''); };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">Payments v2 — Gateway Profiles</h2>
      {error && <div className="mb-3 text-red-600">{error}</div>}

      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm">Gateway Code</label>
          {gatewayMaster.length > 0 ? (
            <select className="border rounded px-3 py-2 w-full" value={form.code} onChange={e=>{
              const code = e.target.value;
              const gm = gatewayMaster.find(g=>String(g.code)===String(code));
              setForm({
                ...form,
                code,
                displayName: form.displayName || gm?.name || code
              });
            }} disabled={!!editingId}>
              <option value="">Select gateway</option>
              {gatewayMaster.filter(g=>g.active!==false).map(g => (
                <option key={g._id} value={g.code}>{g.name} ({g.code})</option>
              ))}
            </select>
          ) : (
            <input className="border rounded px-3 py-2 w-full" value={form.code} onChange={e=>setForm({...form, code:e.target.value})} required disabled={!!editingId} />
          )}
        </div>
        <div>
          <label className="block text-sm">Display Name</label>
          <input className="border rounded px-3 py-2 w-full" value={form.displayName} onChange={e=>setForm({...form, displayName:e.target.value})} />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={!!form.active} onChange={e=>setForm({...form, active:e.target.checked})} />
          <span>Active</span>
        </div>
        <div>
          <label className="block text-sm">Methods (comma separated)</label>
          <input className="border rounded px-3 py-2 w-full" value={form.methods} onChange={e=>setForm({...form, methods:e.target.value})} />
        </div>
        {['capabilities','auth','createPayment','status','refund','refundStatus','refundUpdate','voidPayment','callback','returnUrls'].map(key => (
          <div key={key} className="md:col-span-2">
            <label className="block text-sm capitalize">{key}</label>
            <textarea className="border rounded px-3 py-2 w-full font-mono" rows={key==='createPayment'?6:4} value={form[key]} onChange={e=>setForm({...form, [key]:e.target.value})} />
          </div>
        ))}
        <div className="md:col-span-2 flex gap-3">
          <button type="submit" className="px-4 py-2 bg-black text-white rounded">{editingId? 'Update' : 'Create'}</button>
          <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={resetForm}>Reset</button>
        </div>
      </form>

      <div>
        <h3 className="text-xl font-medium mb-2">Existing Profiles</h3>
        {loading ? <div>Loading...</div> : (
          <table className="min-w-full border">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 py-1 border">Code</th>
                <th className="px-2 py-1 border">Name</th>
                <th className="px-2 py-1 border">Active</th>
                <th className="px-2 py-1 border">Methods</th>
                <th className="px-2 py-1 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it._id}>
                  <td className="px-2 py-1 border">{it.code}</td>
                  <td className="px-2 py-1 border">{it.displayName}</td>
                  <td className="px-2 py-1 border">{it.active? 'Yes':'No'}</td>
                  <td className="px-2 py-1 border">{Array.isArray(it.methods)? it.methods.join(', '): ''}</td>
                  <td className="px-2 py-1 border flex gap-2">
                    <button className="px-2 py-1 bg-gray-200 rounded" onClick={()=>startEdit(it)}>Edit</button>
                    <button className="px-2 py-1 bg-red-600 text-white rounded" onClick={async()=>{ if(window.confirm('Delete profile?')) { await GatewayV2Services.deleteGatewayProfile(it._id); load(); } }}>Delete</button>
                  </td>
                </tr>
              ))}
              {items.length===0 && !loading && (
                <tr><td className="px-2 py-2 text-gray-500" colSpan={5}>No profiles found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
